// src/components/PaymentSheet.jsx
//
// Модалка для записи платежа от/к контрагенту.
//   direction='outgoing' — магазин платит поставщику
//   direction='incoming' — оптовик платит магазину

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { counterparties as cpApi } from '../api';
import { useMyCurrency } from '../hooks/useMyShop';
import { formatMoney } from '../data/currencies';
import Icon from './Icon';
import SafeInput from './SafeInput';

export default function PaymentSheet({
  open,
  counterpartyId,
  counterpartyName,
  direction,                // 'outgoing'|'incoming'
  suggestedAmount = 0,      // долг — предложить заполнить целиком
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const currency = useMyCurrency();

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote]     = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!open) return;
    setAmount('');
    setMethod('cash');
    setNote('');
    setError('');
    setSaving(false);
  }, [open]);

  if (!open) return null;

  const titleKey  = direction === 'outgoing' ? 'cpPayments.payToSupplier' : 'cpPayments.receiveFromClient';
  const isOutgoing = direction === 'outgoing';

  const fillDebt = () => {
    if (suggestedAmount > 0) {
      setAmount(String(suggestedAmount));
    }
  };

  const save = async () => {
    setError('');
    const amt = Number(String(amount).replace(',', '.'));
    if (!isFinite(amt) || amt <= 0) {
      setError(t('cpPayments.errInvalidAmount'));
      return;
    }
    setSaving(true);
    try {
      await cpApi.addPayment(counterpartyId, {
        direction,
        amount: amt,
        method,
        note: note.trim() || undefined,
      });
      onSaved && onSaved();
    } catch (e) {
      setError(e.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          width: '100%', maxWidth: 520,
          maxHeight: '92vh', overflow: 'auto',
          borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
          padding: 20,
        }}
      >
        {/* Шапка */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, flex: 1 }}>
            {t(titleKey)}
          </h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 8, color: 'var(--fg-muted)',
          }}>
            <Icon name="x" size={20} />
          </button>
        </div>

        <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 16 }}>
          {counterpartyName}
        </p>

        {/* Долг */}
        {suggestedAmount > 0 && (
          <div
            onClick={fillDebt}
            style={{
              padding: 14, marginBottom: 14,
              background: 'var(--brand-primary-soft)',
              borderRadius: 'var(--r-md)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <div>
              <p style={{ fontSize: 11, color: 'var(--brand-primary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {isOutgoing ? t('cpPayments.youOwe') : t('cpPayments.theyOwe')}
              </p>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--brand-primary)', marginTop: 2 }}>
                {formatMoney(suggestedAmount, currency)}
              </p>
            </div>
            <button className="btn btn-sm" style={{ background: 'var(--brand-primary)', color: '#fff' }}>
              {t('cpPayments.fillDebt')}
            </button>
          </div>
        )}

        {/* Сумма */}
        <div className="input-group">
          <label>{t('cpPayments.amount')} *</label>
          <SafeInput
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            style={{ fontSize: 20, fontWeight: 700 }}
          />
        </div>

        {/* Метод */}
        <div className="input-group">
          <label>{t('cpPayments.method')}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
            {[
              { code: 'cash',     icon: 'wallet' },
              { code: 'card',     icon: 'cart' },
              { code: 'transfer', icon: 'share' },
              { code: 'other',    icon: 'more' },
            ].map(m => (
              <button
                key={m.code}
                onClick={() => setMethod(m.code)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--r-sm)',
                  border: '1px solid',
                  borderColor: method === m.code ? 'var(--brand-primary)' : 'var(--border)',
                  background: method === m.code ? 'var(--brand-primary-soft)' : 'var(--bg-elevated)',
                  color: method === m.code ? 'var(--brand-primary)' : 'var(--fg-muted)',
                  fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13,
                }}
              >
                <Icon name={m.icon} size={14} />
                {t(`cpPayments.method_${m.code}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Заметка */}
        <div className="input-group">
          <label>{t('cpPayments.note')}</label>
          <SafeInput
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('cpPayments.notePh')}
          />
        </div>

        {error && (
          <div style={{
            padding: 10, marginBottom: 10,
            background: 'var(--danger-soft)',
            color: 'var(--danger)',
            borderRadius: 'var(--r-sm)',
            fontSize: 13,
          }}>{error}</div>
        )}

        <button
          onClick={save}
          disabled={saving}
          className="btn btn-primary btn-lg"
          style={{ width: '100%', marginTop: 8 }}
        >
          {saving ? t('common.saving') : t('cpPayments.savePayment')}
        </button>

        <button
          onClick={onClose}
          disabled={saving}
          className="btn btn-ghost"
          style={{ width: '100%', marginTop: 6 }}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
